import React, { useState } from 'react';
import Drawer from '@mui/material/Drawer';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

const AYUDA = {
    1: {
        titulo: 'Datos de la Venta',
        descripcion: 'Selecciona la bodega y el tipo de consulta para comenzar el proceso.',
        recomendaciones: [
            'Selecciona la bodega desde donde se realizará la venta.',
            'Elige el tipo de consulta que corresponde al producto o servicio solicitado.',
            'Ambos campos son obligatorios para poder continuar al siguiente paso.',
        ],
        errores: [
            'No seleccionar una bodega antes de avanzar.',
            'Elegir un tipo de consulta que no corresponde al producto.',
        ],
        ejemplo: null,
    },
    2: {
        titulo: 'Verificación de Identidad',
        descripcion: 'Valida la cédula del cliente antes de continuar.',
        recomendaciones: [
            'Ingresa los 10 dígitos de la cédula del cliente, sin espacios ni guiones.',
            'Presiona el botón "Verificar" o espera que se valide automáticamente.',
            'Asegúrate de ver la confirmación de identidad verificada antes de continuar.',
        ],
        errores: [
            'Ingresar una cédula con menos o más de 10 dígitos.',
            'Avanzar sin esperar a que el sistema valide la información.',
        ],
        ejemplo: null,
    },
    3: {
        titulo: 'Datos Personales',
        descripcion: 'Verifica los datos obtenidos automáticamente del registro civil y del cónyuge si aplica.',
        recomendaciones: [
            'Todos los datos en esta sección (Nombres, Apellidos, Estado Civil, Género) se recuperan automáticamente y están bloqueados por seguridad.',
            'Si el estado civil es "Casado/a", los datos del cónyuge (Cédula y Nombres) se autocompletarán y estarán bloqueados.',
            'El Teléfono y los Ingresos del cónyuge sí deben ser ingresados manualmente y son obligatorios.',
            'El único campo extra que debes ingresar es "Cargas Familiares".',
            'Las cargas familiares deben ser un número entero.',
        ],
        errores: [
            'Ingresar un valor negativo o letras en las Cargas Familiares.',
            'Olvidar ingresar el teléfono o ingresos del cónyuge cuando el cliente es casado.',
        ],
        ejemplo: {
            titulo: 'Ejemplo de Cargas Familiares',
            contenido: 'Cargas Familiares: 2\n(Solo ingresa el número)',
        },
    },
    4: {
        titulo: 'Contacto y Situación Laboral',
        descripcion: 'Ingresa la información de contacto e ingresos. Todos los campos aquí son obligatorios.',
        recomendaciones: [
            'El celular debe tener exactamente 10 dígitos (solo números).',
            'El correo electrónico es obligatorio y debe tener formato válido (@).',
            'Si la formalidad es "Informal", debes seleccionar obligatoriamente un Tipo de Rubro.',
            'El ingreso declarado debe ser un valor mayor a $0.',
            'Selecciona el Tipo de Vivienda y las fechas desde cuándo reside y trabaja ahí.',
        ],
        errores: [
            'Dejar cualquier campo vacío (todo es obligatorio).',
            'Ingresar un celular con menos de 10 dígitos.',
            'No seleccionar el Tipo de Rubro cuando indicaste que es Informal.',
        ],
        ejemplo: null,
    },
    5: {
        titulo: 'Direcciones',
        descripcion: 'Registra obligatoriamente un Domicilio y un Trabajo.',
        recomendaciones: [
            'Debes agregar al menos dos direcciones para poder continuar.',
            'Una dirección debe ser de tipo "Domicilio" y la otra de tipo "Trabajo".',
            'No puedes repetir los tipos de dirección (no puedes ingresar dos domicilios o dos trabajos).',
            'Los campos Dirección, Referencia, Tipo y Sector/Ubicación son obligatorios en ambos registros.',
            'Asegúrate de tener encendido el GPS (Ubicación) de tu dispositivo.',
            'Debes conceder los permisos de ubicación al navegador cuando te los solicite para poder capturar las coordenadas.',
        ],
        errores: [
            'Olvidar ingresar la dirección de Trabajo o la de Domicilio.',
            'Repetir el mismo tipo de dirección dos veces.',
            'Dejar en blanco el Sector/Ubicación o la Referencia en cualquiera de las direcciones.',
            'Tener el GPS apagado o denegar los permisos de ubicación en el navegador.',
        ],
        ejemplo: {
            titulo: 'Ejemplo de registros requeridos',
            contenido: 'Registro 1:\n- Tipo: Domicilio\n- Dirección: Av. Amazonas y Roca\n- Ubicación: Pichincha\n\nRegistro 2:\n- Tipo: Trabajo\n- Dirección: Centro Comercial Recreo\n- Ubicación: Pichincha',
        },
    },
    6: {
        titulo: 'Referencias Personales',
        descripcion: 'Ingresa 2 personas de contacto diferentes.',
        recomendaciones: [
            'Es obligatorio registrar exactamente 2 referencias.',
            'Los teléfonos deben tener 10 dígitos y no pueden repetirse entre las referencias.',
            'Selecciona correctamente la relación que tiene el cliente con la referencia.',
            'Asegúrate de escribir correctamente los nombres y apellidos.',
        ],
        errores: [
            'Intentar continuar con solo 1 referencia ingresada.',
            'Repetir el mismo número de teléfono para ambas referencias.',
            'Ingresar teléfonos incompletos.',
        ],
        ejemplo: null,
    },
    7: {
        titulo: 'Aceptación Legal',
        descripcion: 'Revisa y acepta los términos antes de enviar.',
        recomendaciones: [
            'Debes aceptar obligatoriamente los Términos y Condiciones (primera casilla).',
            'Debes aceptar obligatoriamente las Políticas de Privacidad (segunda casilla).',
            'Verifica todo el resumen antes de hacer clic en "Enviar Solicitud".',
            'Si necesitas cambiar algo, usa el botón "Editar información" debajo del resumen.',
        ],
        errores: [
            'Hacer clic en Enviar sin haber marcado ambas casillas de aceptación.',
        ],
        ejemplo: null,
    },
};

const AyudaSolicitudYupana = ({ stepActual }) => {
    const [open, setOpen] = useState(false);
    const ayuda = AYUDA[stepActual] || AYUDA[1];

    return (
        <>
            {/* Botón trigger */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                title={`Ayuda para el paso ${stepActual}`}
                className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 active:bg-white/40 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-all duration-200 flex-shrink-0"
            >
                <HelpOutlineIcon sx={{ fontSize: 15 }} />
                <span className="hidden sm:inline">Ayuda</span>
            </button>

            {/* Drawer lateral derecho */}
            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '100vw', sm: 400 },
                        maxWidth: '100vw',
                        boxShadow: '0 -4px 32px rgba(0,0,0,0.18)',
                    },
                }}
            >
                <div className="flex flex-col h-full bg-white">

                    {/* Header */}
                    <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-blue-700 to-blue-600 flex-shrink-0">
                        <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <HelpOutlineIcon sx={{ fontSize: 20, color: 'white' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest">Ayuda — Paso {stepActual} de 7</p>
                            <h3 className="text-white font-bold text-sm leading-tight">{ayuda.titulo}</h3>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
                        >
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </button>
                    </div>

                    {/* Contenido scrollable */}
                    <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

                        {/* Descripción intro */}
                        <p className="text-sm text-slate-600 leading-relaxed">{ayuda.descripcion}</p>

                        {/* Recomendaciones paso a paso */}
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Cómo completar este paso</span>
                            </div>
                            <ol className="space-y-2.5">
                                {ayuda.recomendaciones.map((rec, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full flex items-center justify-center mt-0.5 select-none">
                                            {i + 1}
                                        </span>
                                        <span className="text-sm text-slate-600 leading-relaxed">{rec}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        {/* Errores frecuentes */}
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <WarningAmberIcon sx={{ fontSize: 16, color: '#d97706' }} />
                                <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Errores frecuentes</span>
                            </div>
                            <ul className="space-y-2">
                                {ayuda.errores.map((err, i) => (
                                    <li key={i} className="flex items-start gap-2.5 text-sm text-amber-800">
                                        <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 bg-amber-400 rounded-full" />
                                        {err}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Ejemplo */}
                        {ayuda.ejemplo && (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <LightbulbOutlinedIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{ayuda.ejemplo.titulo}</span>
                                </div>
                                <pre className="text-sm text-slate-700 font-mono bg-white border border-slate-200 rounded-lg px-4 py-3 whitespace-pre-wrap leading-relaxed">
                                    {ayuda.ejemplo.contenido}
                                </pre>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="w-full py-2.5 text-sm font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
                        >
                            Cerrar ayuda
                        </button>
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default AyudaSolicitudYupana;
